import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { listPublicRegions, publicRegionSet, type PublicRegionCard, type PublicRegion } from '@/api/publicRegions'

// A public, anonymous constellation of curated regions. The visitor toggles
// which published regions share the sky — serious domains beside playful ones.
// Reuses the constellation palette; the full 2D/3D unification is P16-T06.

// domain-slug -> a glow, cycled from the constellation palette
const PALETTE = ['#60a5fa', '#f0a35e', '#2dd4bf', '#a78bfa', '#4ade80', '#e2bc77', '#c0392b']
const STAR_CORE = '#e8e2d9'

interface Node {
  id: string
  name: string
  type: string
  region: string
  x: number
  y: number
  vx: number
  vy: number
}

function Sky({ regions, colorOf }: { regions: PublicRegion[]; colorOf: (slug: string) => string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [hover, setHover] = useState<Node | null>(null)
  const stateRef = useRef<{ nodes: Node[]; edges: [number, number][] }>({ nodes: [], edges: [] })

  // build nodes/edges once per region set; cluster each region around a ring anchor
  useEffect(() => {
    const nodes: Node[] = []
    const index = new Map<string, number>()
    const nRegions = Math.max(1, regions.length)
    regions.forEach((region, ri) => {
      const anchorA = (ri / nRegions) * Math.PI * 2
      const ax = Math.cos(anchorA) * 220
      const ay = Math.sin(anchorA) * 220
      region.entities.forEach((e) => {
        index.set(`${region.slug}:${e.id}`, nodes.length)
        nodes.push({
          id: e.id, name: e.name, type: e.type, region: region.slug,
          x: ax + (Math.random() - 0.5) * 260, y: ay + (Math.random() - 0.5) * 260,
          vx: 0, vy: 0,
        })
      })
    })
    const edges: [number, number][] = []
    regions.forEach((region) => {
      region.relations.forEach((r) => {
        const a = index.get(`${region.slug}:${r.from}`)
        const b = index.get(`${region.slug}:${r.to}`)
        if (a !== undefined && b !== undefined && a !== b) edges.push([a, b])
      })
    })
    stateRef.current = { nodes, edges }
  }, [regions])

  // force sim + render loop
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')!
    let raf = 0
    let alpha = 1

    function resize() {
      const dpr = window.devicePixelRatio || 1
      const w = canvas!.clientWidth, h = canvas!.clientHeight
      canvas!.width = w * dpr; canvas!.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    function step() {
      const { nodes, edges } = stateRef.current
      const w = canvas!.clientWidth, h = canvas!.clientHeight
      const cx = w / 2, cy = h / 2
      // repulsion (capped pairs for perf), edge springs, gentle centering
      const N = nodes.length
      for (let i = 0; i < N; i++) {
        const a = nodes[i]
        for (let j = i + 1; j < N; j++) {
          const b = nodes[j]
          let dx = a.x - b.x, dy = a.y - b.y
          let d2 = dx * dx + dy * dy
          if (d2 < 1) { d2 = 1; dx = Math.random(); dy = Math.random() }
          if (d2 > 90000) continue
          const f = (alpha * 900) / d2
          const d = Math.sqrt(d2)
          a.vx += (dx / d) * f; a.vy += (dy / d) * f
          b.vx -= (dx / d) * f; b.vy -= (dy / d) * f
        }
      }
      for (const [ai, bi] of edges) {
        const a = nodes[ai], b = nodes[bi]
        const dx = b.x - a.x, dy = b.y - a.y
        const d = Math.sqrt(dx * dx + dy * dy) || 1
        const f = alpha * 0.02 * (d - 70)
        a.vx += (dx / d) * f; a.vy += (dy / d) * f
        b.vx -= (dx / d) * f; b.vy -= (dy / d) * f
      }
      for (const n of nodes) {
        n.vx += (cx - (cx + n.x)) * 0 // no-op keep-local; center via translate below
        n.vx *= 0.82; n.vy *= 0.82
        n.x += n.vx; n.y += n.vy
      }
      alpha *= 0.994
      if (alpha < 0.05) alpha = 0.05

      // draw
      ctx.clearRect(0, 0, w, h)
      ctx.save()
      ctx.translate(cx, cy)
      // edges
      ctx.strokeStyle = 'rgba(201,169,110,0.18)'
      ctx.lineWidth = 1
      for (const [ai, bi] of edges) {
        const a = nodes[ai], b = nodes[bi]
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
      }
      // stars
      for (const n of nodes) {
        const glow = colorOf(n.region)
        ctx.beginPath()
        ctx.arc(n.x, n.y, 9, 0, Math.PI * 2)
        ctx.fillStyle = glow + '33'; ctx.fill()
        ctx.beginPath()
        ctx.arc(n.x, n.y, 3.2, 0, Math.PI * 2)
        ctx.fillStyle = STAR_CORE; ctx.fill()
        ctx.strokeStyle = glow; ctx.lineWidth = 1.2; ctx.stroke()
      }
      ctx.restore()
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [regions, colorOf])

  function onMove(e: React.MouseEvent) {
    const canvas = canvasRef.current!
    const rect = canvas.getBoundingClientRect()
    const mx = e.clientX - rect.left - rect.width / 2
    const my = e.clientY - rect.top - rect.height / 2
    let best: Node | null = null, bestD = 16 * 16
    for (const n of stateRef.current.nodes) {
      const d = (n.x - mx) ** 2 + (n.y - my) ** 2
      if (d < bestD) { bestD = d; best = n }
    }
    setHover(best)
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <canvas ref={canvasRef} onMouseMove={onMove} onMouseLeave={() => setHover(null)}
              style={{ width: '100%', height: '100%', display: 'block', cursor: 'crosshair' }} />
      {hover && (
        <div style={{
          position: 'absolute', top: 12, left: 12, padding: '6px 10px', fontSize: 13,
          background: 'rgba(10,10,14,0.85)', border: '1px solid rgba(201,169,110,0.4)',
          borderRadius: 6, pointerEvents: 'none',
        }}>
          <strong>{hover.name}</strong>
          <span style={{ opacity: 0.6 }}> · {hover.type} · {hover.region}</span>
        </div>
      )}
    </div>
  )
}

export function PublicRegions() {
  const [cards, setCards] = useState<PublicRegionCard[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState<Set<string>>(new Set())
  const [regions, setRegions] = useState<PublicRegion[]>([])

  const colorFor = useMemo(() => {
    const map = new Map<string, string>()
    ;(cards ?? []).forEach((c, i) => map.set(c.slug, PALETTE[i % PALETTE.length]))
    return (slug: string) => map.get(slug) ?? '#888'
  }, [cards])

  useEffect(() => {
    listPublicRegions()
      .then((cs) => {
        setCards(cs)
        setActive(new Set(cs.slice(0, 3).map((c) => c.slug))) // open on the first few
      })
      .catch(() => setError("Couldn't load the public domains."))
  }, [])

  useEffect(() => {
    if (!cards) return
    const slugs = [...active]
    if (slugs.length === 0) { setRegions([]); return }
    publicRegionSet(slugs).then(setRegions).catch(() => setError("Couldn't load the sky."))
  }, [active, cards])

  function toggle(slug: string) {
    setActive((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug); else next.add(slug)
      return next
    })
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
                  background: 'radial-gradient(1200px 800px at 50% -10%, #14121a, #08070b)', color: '#e8e2d9' }}>
      <header style={{ padding: '20px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: 'Cinzel, serif', fontSize: 22, letterSpacing: '0.06em' }}>Diwata · Public Constellations</div>
          <div style={{ fontSize: 13, opacity: 0.65, marginTop: 2 }}>
            Curated worlds, open to wander. Toggle which domains share the sky.
          </div>
        </div>
        <Link to="/login" style={{ color: '#c9a96e', fontSize: 14, textDecoration: 'none',
              border: '1px solid rgba(201,169,110,0.5)', borderRadius: 999, padding: '8px 18px' }}>
          Build your own →
        </Link>
      </header>

      {error && <div style={{ padding: '0 28px', color: '#e0564a' }}>{error}</div>}

      <div style={{ padding: '0 28px 12px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(cards ?? []).map((c) => {
          const on = active.has(c.slug)
          return (
            <button key={c.slug} type="button" onClick={() => toggle(c.slug)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer',
                       padding: '6px 12px', borderRadius: 999, color: on ? '#08070b' : '#e8e2d9',
                       background: on ? colorFor(c.slug) : 'transparent',
                       border: `1px solid ${colorFor(c.slug)}`, transition: 'all .12s' }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, background: on ? '#08070b' : colorFor(c.slug) }} />
              {c.name}
            </button>
          )
        })}
        {cards && cards.length === 0 && <div style={{ opacity: 0.6, fontSize: 14 }}>No public domains yet — check back soon.</div>}
      </div>

      <div style={{ flex: 1, minHeight: 420, margin: '0 20px 20px', borderRadius: 12,
                    border: '1px solid rgba(201,169,110,0.15)', overflow: 'hidden' }}>
        {regions.length > 0
          ? <Sky regions={regions} colorOf={colorFor} />
          : <div style={{ display: 'grid', placeItems: 'center', height: '100%', opacity: 0.5, fontSize: 14 }}>
              {active.size === 0 ? 'Pick a domain above to light the sky.' : 'Lighting the sky…'}
            </div>}
      </div>
    </div>
  )
}
